/**
 * Feuille « wide » — layout PAYSAGE (2-3 colonnes), pour les gros menus (>=
 * `LARGE_MAX_OFFERS` offres, cf `autoLayout.ts` — cas réel : "Le nouveau
 * menu", 20 offres, illisible en grand format 2 colonnes). Même squelette que
 * `LargeSheet.tsx` (en-tête photo + titre, règle, colonnes de sections,
 * footer) et même `SheetColumn`/`LargePalette` — seule la largeur de
 * conception (1080px au lieu de 600) et le nombre de colonnes
 * (`data.columns.length`, 2 ou 3 selon `buildWideData`) changent. Les 3
 * styles (Boudoir/Éditorial/Pop) réutilisent donc les mêmes palettes que le
 * grand format, cf `wideTemplates.tsx`.
 *
 * Styles inline (pas de Tailwind) : ce noeud est la cible directe de
 * `domToPng`, palette FIXE déconnectée du thème clair/sombre du cockpit —
 * même contrat que `LargeSheet.tsx`.
 */
import { forwardRef, type CSSProperties } from "react";

import type { MenuWideData } from "../types";
import { PortraitPlaceholder } from "./PortraitPlaceholder";
import { SheetColumn } from "./LargeSheet";
import type { LargePalette } from "./largePalettes";

/** Feuille wide paramétrée par palette. La ref pointe le noeud racine
 *  (cible `domToPng`). */
export const WideSheetView = forwardRef<
  HTMLDivElement,
  { data: MenuWideData; palette: LargePalette }
>(function WideSheetView({ data, palette }, ref) {
  const titleStyle: CSSProperties = {
    fontFamily: "Georgia, serif",
    fontWeight: 400,
    letterSpacing: palette.titleUppercase ? "0.02em" : "-0.01em",
    fontSize: 46,
    lineHeight: 0.95,
    margin: 0,
    textTransform: palette.titleUppercase ? "uppercase" : "none",
  };
  return (
    <div
      ref={ref}
      style={{
        boxSizing: "border-box",
        width: "100%",
        borderRadius: 14,
        overflow: "hidden",
        padding: "36px 40px 30px",
        background: palette.background,
        color: palette.color,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
        <div>
          <h2 style={titleStyle}>{data.name}</h2>
        </div>
        <div
          style={{
            width: 128,
            height: 128,
            borderRadius: 10,
            overflow: "hidden",
            flex: "none",
            border: `1px solid ${palette.portraitBorder}`,
          }}
        >
          {data.photoUrl ? (
            <img
              src={data.photoUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <PortraitPlaceholder bg={palette.portraitBg} fg={palette.portraitFg} shape="square" />
          )}
        </div>
      </div>

      <div style={{ height: 1, margin: "22px 0 4px", background: palette.ruleColor }} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${data.columns.length}, 1fr)`,
          gap: "26px 34px",
        }}
      >
        {data.columns.map((sections, i) => (
          // Colonnes équilibrées, position stable dans le rendu (pas une
          // liste réordonnable) — index de colonne comme clé, sans risque.
          <SheetColumn key={i} sections={sections} palette={palette} />
        ))}
      </div>

      <div
        style={{
          marginTop: 22,
          textAlign: "center",
          fontSize: 10,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: palette.footColor,
        }}
      >
        {/* Incident Trista 24-07 : ce footer promettait « écris-moi le
            numéro » alors qu'AUCUN template n'imprime de numéro d'item
            (SheetColumn = nom + prix seulement) — le fan répondait « celui
            de 39 » et le backend ne savait pas router. Même libellé que
            LargeSheet : nom ou prix, les deux routes que le backend matche
            réellement (menu_selection.py). Les PNG déjà uploadés gardent
            l'ancien texte tant que la variante n'est pas régénérée. */}
        Choisis · dis-moi ce qui te tente · je m'occupe du reste
      </div>
    </div>
  );
});
